import React, { useState } from 'react';
import { MenuIcon, XIcon, ChevronDownIcon } from 'lucide-react';
import { useLanguage } from '../hooks/useLanguage';
import { useNavigate, Link, useLocation } from 'react-router-dom';
export function Header({
  variant = 'light'
}: {
  variant?: 'light' | 'dark' | 'transparent-dark';
}) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const {
    t,
    locale,
    direction
  } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const aboutPath = locale === 'ar' ? '/ar/about' : '/about';
  const cardIssuingPath = locale === 'ar' ? '/ar/services/card-issuing' : '/services/card-issuing';
  const atmAcquiringPath = locale === 'ar' ? '/ar/services/atm-acquiring' : '/services/atm-acquiring';
  const merchantAcquiringPath = locale === 'ar' ? '/ar/services/merchant-acquiring' : '/services/merchant-acquiring';
  const mobilePaymentsPath = locale === 'ar' ? '/ar/services/mobile-payments' : '/services/mobile-payments';
  const consultancyPath = locale === 'ar' ? '/ar/services/consultancy' : '/services/consultancy';
  const otherServicesPath = locale === 'ar' ? '/ar/services/other-services' : '/services/other-services';
  const ourToolsPath = locale === 'ar' ? '/ar/services/our-tools' : '/services/our-tools';
  const contactPath = locale === 'ar' ? '/ar/contact' : '/contact';
  const partnersPath = locale === 'ar' ? '/ar/partners' : '/partners';
  const corporateGovernancePath = locale === 'ar' ? '/ar/corporate-governance' : '/corporate-governance';
  const advisorsAuditorsPath = locale === 'ar' ? '/ar/advisors-auditors' : '/advisors-auditors';
  const isInnerPage = variant === 'dark';
  const isTransparentDark = variant === 'transparent-dark';
  const logoSrc = variant === 'light' ? 'https://cdn.magicpatterns.com/uploads/pGgoddoE2KXfnRu1yQ5keh/logo.svg' : 'https://cdn.magicpatterns.com/uploads/evj8yZ2b7sjfawCcGZc7Ao/logo-dark.svg';
  const textColor = variant === 'light' ? 'text-white' : 'text-[#061D76]';
  const textHoverColor = 'hover:text-[#25b8d5]';
  const dividerColor = variant === 'light' ? 'bg-white/20' : 'bg-[#061D76]/20';
  const langActiveColor = variant === 'light' ? 'text-[#25b8d5]' : 'text-[#25b8d5]';
  const langInactiveColor = variant === 'light' ? 'text-white/60 hover:text-[#25b8d5]' : 'text-[#061D76]/60 hover:text-[#25b8d5]';
  const langSeparatorColor = variant === 'light' ? 'text-white/40' : 'text-[#061D76]/40';
  const menuIconColor = variant === 'light' ? 'text-white' : 'text-[#061D76]';
  const switchLanguage = (lang: 'en' | 'ar') => {
    const currentPath = location.pathname;
    // Strip existing /ar prefix to get the base path
    const basePath = currentPath.replace(/^\/ar/, '') || '/';
    if (lang === 'ar') {
      navigate(basePath === '/' ? '/ar' : `/ar${basePath}`);
    } else {
      navigate(basePath);
    }
    setIsMenuOpen(false);
  };
  const groupMenuItems = [{
    label: t('header.about'),
    href: aboutPath
  }, {
    label: t('header.corporateGovernance'),
    href: corporateGovernancePath
  }, {
    label: t('header.advisorsAuditors'),
    href: advisorsAuditorsPath
  }];
  const businessMenuItems = [{
    label: t('header.cardIssuing'),
    href: cardIssuingPath
  }, {
    label: t('header.atmAcquiring'),
    href: atmAcquiringPath
  }, {
    label: t('header.merchantAcquiring'),
    href: merchantAcquiringPath
  }, {
    label: t('header.mobilePayment'),
    href: mobilePaymentsPath
  }, {
    label: t('header.consultancy'),
    href: consultancyPath
  }, {
    label: t('header.otherServices'),
    href: otherServicesPath
  }];
  return <header className={`absolute top-0 left-0 w-full z-50 px-6 py-5 md:px-16 lg:px-24 flex items-center ${isInnerPage ? 'bg-white shadow-sm' : 'bg-transparent'} ${textColor}`} dir={direction} data-id="element-229">
      {/* Logo - far left (or right in RTL) */}
      <div className="flex items-center" data-id="element-230">
        <Link to={locale === 'ar' ? '/ar' : '/'} data-id="element-231">
          <img src={logoSrc} alt="CSC Group" className="h-10 w-auto" data-id="element-232" />
        </Link>
      </div>

      {/* Spacer to push everything to the other side */}
      <div className="flex-1" data-id="element-233" />

      {/* Desktop: Nav + Language + Icons all grouped */}
      <div className="hidden lg:flex items-center gap-8" data-id="element-234">
        {/* Navigation */}
        <nav className="flex items-center gap-10 text-sm font-semibold tracking-wide" data-id="element-235">
          {/* The Group - with dropdown */}
          <div className="relative" onMouseEnter={() => setActiveDropdown('group')} onMouseLeave={() => setActiveDropdown(null)} data-id="element-236">
            <a href="#" className={`${textHoverColor} transition-colors py-2 block`} data-id="element-237">
              {t('header.theGroup')}
            </a>

            {activeDropdown === 'group' && <>
                <span className="absolute left-0 w-full h-[3px] bg-[#25B8D6] z-10" data-id="element-238" />
                <div className={`absolute top-full pt-[3px] ${direction === 'rtl' ? 'right-0' : 'left-0'}`} data-id="element-239">
                  <div className="bg-white min-w-[280px] shadow-xl overflow-hidden" style={{
                borderRadius: direction === 'rtl' ? '0 0 0 50px' : '0 0 50px 0'
              }} data-id="element-240">
                    <div className="py-0" data-id="element-241">
                      {groupMenuItems.map((item, index) => item.href === '#' ? <a key={index} href="#" className="block text-[#061d76] font-semibold text-base py-3 px-8 hover:bg-[#25B8D6] hover:text-white transition-colors text-start" data-id="element-242">
                            {item.label}
                          </a> : <Link key={index} to={item.href} className="block text-[#061d76] font-semibold text-base py-3 px-8 hover:bg-[#25B8D6] hover:text-white transition-colors text-start" data-id="element-243">
                            {item.label}
                          </Link>)}
                    </div>
                  </div>
                </div>
              </>}
          </div>

          {/* Businesses - with dropdown */}
          <div className="relative" onMouseEnter={() => setActiveDropdown('businesses')} onMouseLeave={() => setActiveDropdown(null)} data-id="element-244">
            <a href="#" className={`${textHoverColor} transition-colors py-2 block`} data-id="element-245">
              {t('header.businesses')}
            </a>

            {activeDropdown === 'businesses' && <>
                <span className="absolute left-0 w-full h-[3px] bg-[#25B8D6] z-10" data-id="element-246" />
                <div className={`absolute top-full pt-[3px] ${direction === 'rtl' ? 'right-0' : 'left-0'}`} data-id="element-247">
                  <div className="bg-white min-w-[280px] shadow-xl overflow-hidden" style={{
                borderRadius: direction === 'rtl' ? '0 0 0 50px' : '0 0 50px 0'
              }} data-id="element-248">
                    <div className="py-0" data-id="element-249">
                      {businessMenuItems.map((item, index) => item.href === '#' ? <a key={index} href="#" className="block text-[#061d76] font-semibold text-base py-3 px-8 hover:bg-[#25B8D6] hover:text-white transition-colors text-start" data-id="element-250">
                            {item.label}
                          </a> : <Link key={index} to={item.href} className="block text-[#061d76] font-semibold text-base py-3 px-8 hover:bg-[#25B8D6] hover:text-white transition-colors text-start" data-id="element-251">
                            {item.label}
                          </Link>)}
                      <Link to={ourToolsPath} className="block text-[#061d76] font-semibold text-base py-3 px-8 hover:bg-[#25B8D6] hover:text-white transition-colors text-start" data-id="element-252">
                        {t('header.ourTools')}
                      </Link>
                    </div>
                  </div>
                </div>
              </>}
          </div>

          <Link to={partnersPath} className={`${textHoverColor} transition-colors`} data-id="element-253">
            {t('header.partners')}
          </Link>
          <Link to={contactPath} className={`${textHoverColor} transition-colors`} data-id="element-254">
            {t('header.contact')}
          </Link>
        </nav>

        {/* Divider */}
        <div className={`w-px h-6 ${dividerColor}`} data-id="element-255" />

        {/* Language Selectors */}
        <div className="flex items-center gap-2 text-sm font-medium" data-id="element-256">
          <button onClick={() => switchLanguage('en')} className={`${locale === 'en' ? langActiveColor : langInactiveColor} transition-colors`} data-id="element-257">
            En
          </button>
          <span className={langSeparatorColor} data-id="element-258">|</span>
          <button onClick={() => switchLanguage('ar')} className={`${locale === 'ar' ? langActiveColor : langInactiveColor} transition-colors`} data-id="element-259">
            AR
          </button>
        </div>

        {/* Icon Buttons */}
        <div className="flex items-center gap-3" data-id="element-260">
          <a href="#" className="w-10 h-10 rounded-full bg-[#25b8d5] flex items-center justify-center hover:bg-white hover:scale-110 transition-all duration-300 group" aria-label="Home" data-id="element-261">
            <img src="https://cdn.magicpatterns.com/uploads/mJm2cCyBBxRzfedF4ndUce/homeicon.svg" alt="" className="w-5 h-5 group-hover:brightness-0 transition-all" data-id="element-262" />
          </a>
          <a href="#" className="w-10 h-10 rounded-full bg-[#25b8d5] flex items-center justify-center hover:bg-white hover:scale-110 transition-all duration-300 group" aria-label="Card services" data-id="element-263">
            <img src="https://cdn.magicpatterns.com/uploads/rr3bWdmyzs8eihXcWN94ix/card-icon.svg" alt="" className="w-5 h-5 group-hover:brightness-0 transition-all" data-id="element-264" />
          </a>
        </div>
      </div>

      {/* Mobile Menu Button - only show when menu is closed */}
      {!isMenuOpen && <button className={`lg:hidden p-2 ${menuIconColor}`} onClick={() => setIsMenuOpen(true)} aria-label="Open menu" data-id="element-265">
          <MenuIcon className="w-6 h-6" data-id="element-266" />
        </button>}

      {/* Mobile Menu - Full Screen */}
      {isMenuOpen && <div className="fixed inset-0 bg-[#061d76] z-50 flex flex-col lg:hidden mobile-menu-enter" dir={direction} data-id="element-267">
          {/* Header area with logo and close */}
          <div className="flex items-center justify-between px-6 py-5 mobile-menu-item" style={{
        animationDelay: '150ms'
      }} data-id="element-268">
            <img src="https://cdn.magicpatterns.com/uploads/pGgoddoE2KXfnRu1yQ5keh/logo.svg" alt="CSC Group" className="h-10 w-auto" data-id="element-269" />
            <button className="p-2 text-white" onClick={() => setIsMenuOpen(false)} aria-label="Close menu" data-id="element-270">
              <XIcon className="w-6 h-6" data-id="element-271" />
            </button>
          </div>

          {/* Nav links - centered vertically */}
          <div className="flex-1 flex flex-col justify-center px-10 gap-2 overflow-y-auto" data-id="element-272">
            {/* The Group - expandable */}
            <div className="mobile-menu-item" style={{
          animationDelay: '200ms'
        }} data-id="element-273">
              <button className="text-2xl font-bold text-white hover:text-[#25b8d5] transition-colors w-full text-start py-3 flex items-center justify-between" onClick={() => setMobileExpanded(mobileExpanded === 'group' ? null : 'group')} data-id="element-274">
                {t('header.theGroup')}
                <ChevronDownIcon className={`w-5 h-5 transition-transform duration-300 ${mobileExpanded === 'group' ? 'rotate-180' : ''}`} data-id="element-275" />
              </button>
              <div className={`overflow-hidden transition-all duration-300 ${mobileExpanded === 'group' ? 'max-h-60 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-276">
                <div className="px-4 pb-4 flex flex-col gap-3" data-id="element-277">
                  {groupMenuItems.map((item, index) => item.href === '#' ? <a key={index} href="#" className="text-base text-white hover:text-[#25b8d5] transition-colors text-start" onClick={() => setIsMenuOpen(false)} data-id="element-278">
                        {item.label}
                      </a> : <Link key={index} to={item.href} className="text-base text-white hover:text-[#25b8d5] transition-colors text-start" onClick={() => setIsMenuOpen(false)} data-id="element-279">
                        {item.label}
                      </Link>)}
                </div>
              </div>
            </div>

            {/* Businesses - expandable */}
            <div className="mobile-menu-item" style={{
          animationDelay: '300ms'
        }} data-id="element-280">
              <button className="text-2xl font-bold text-white hover:text-[#25b8d5] transition-colors w-full text-start py-3 flex items-center justify-between" onClick={() => setMobileExpanded(mobileExpanded === 'businesses' ? null : 'businesses')} data-id="element-281">
                {t('header.businesses')}
                <ChevronDownIcon className={`w-5 h-5 transition-transform duration-300 ${mobileExpanded === 'businesses' ? 'rotate-180' : ''}`} data-id="element-282" />
              </button>
              <div className={`overflow-hidden transition-all duration-300 ${mobileExpanded === 'businesses' ? 'max-h-80 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-283">
                <div className="px-4 pb-4 flex flex-col gap-3" data-id="element-284">
                  {businessMenuItems.map((item, index) => item.href === '#' ? <a key={index} href="#" className="text-base text-white hover:text-[#25b8d5] transition-colors text-start" onClick={() => setIsMenuOpen(false)} data-id="element-285">
                        {item.label}
                      </a> : <Link key={index} to={item.href} className="text-base text-white hover:text-[#25b8d5] transition-colors text-start" onClick={() => setIsMenuOpen(false)} data-id="element-286">
                        {item.label}
                      </Link>)}
                  <Link to={ourToolsPath} className="text-base text-white hover:text-[#25b8d5] transition-colors text-start" onClick={() => setIsMenuOpen(false)} data-id="element-287">
                    {t('header.ourTools')}
                  </Link>
                </div>
              </div>
            </div>

            <a href="#" className="text-2xl font-bold text-white hover:text-[#25b8d5] transition-colors py-3 mobile-menu-item text-start" style={{
          animationDelay: '400ms'
        }} onClick={() => setIsMenuOpen(false)} data-id="element-288">
              {t('header.partners')}
            </a>
            <Link to={contactPath} className="text-2xl font-bold text-white hover:text-[#25b8d5] transition-colors py-3 mobile-menu-item text-start" style={{
          animationDelay: '500ms'
        }} onClick={() => setIsMenuOpen(false)} data-id="element-289">
              {t('header.contact')}
            </Link>
          </div>

          {/* Bottom area - language + icons */}
          <div className="px-10 pb-10 flex flex-col gap-6 mobile-menu-item" style={{
        animationDelay: '600ms'
      }} data-id="element-290">
            <div className="w-full h-px bg-white/10" data-id="element-291" />
            <div className="flex items-center justify-between" data-id="element-292">
              <div className="flex items-center gap-3 text-sm font-medium" data-id="element-293">
                <button onClick={() => switchLanguage('en')} className={`${locale === 'en' ? langActiveColor : langInactiveColor} transition-colors`} data-id="element-294">
                  En
                </button>
                <span className={langSeparatorColor} data-id="element-295">|</span>
                <button onClick={() => switchLanguage('ar')} className={`${locale === 'ar' ? langActiveColor : langInactiveColor} transition-colors`} data-id="element-296">
                  AR
                </button>
              </div>
              <div className="flex items-center gap-3" data-id="element-297">
                <a href="#" className="w-10 h-10 rounded-full bg-[#25b8d5] flex items-center justify-center" data-id="element-298">
                  <img src="https://cdn.magicpatterns.com/uploads/mJm2cCyBBxRzfedF4ndUce/homeicon.svg" alt="" className="w-5 h-5" data-id="element-299" />
                </a>
                <a href="#" className="w-10 h-10 rounded-full bg-[#25b8d5] flex items-center justify-center" data-id="element-300">
                  <img src="https://cdn.magicpatterns.com/uploads/rr3bWdmyzs8eihXcWN94ix/card-icon.svg" alt="" className="w-5 h-5" data-id="element-301" />
                </a>
              </div>
            </div>
          </div>
        </div>}
    </header>;
}